import { isDefined } from "@clipboard-health/util-ts";
import { Badge as MuiBadge, type BadgeProps as MuiBadgeProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

export const BADGE_SIZES = ["small", "medium"] as const;
export type BadgeSize = (typeof BADGE_SIZES)[number];

interface BadgeProps extends Pick<
  MuiBadgeProps,
  "invisible" | "max" | "showZero" | "overlap" | "anchorOrigin" | "sx"
> {
  count?: number;
  /**
   * Explicit badge content, e.g. a short label like "New". Overrides `count` when provided.
   */
  content?: React.ReactNode;
  children?: React.ReactNode;
  size?: BadgeSize;
  /**
   * The color of the border of the badge.
   * Defaults to base.base.
   */
  borderColor?: (theme: Theme) => string;
  /**
   * The background color of the badge.
   * Defaults to error.main (red).
   */
  backgroundColor?: (theme: Theme) => string;
  /**
   * The color of the text of the badge.
   * Defaults to error.contrastText (white).
   */
  color?: (theme: Theme) => string;
}

/**
 * Simple component wrapping the MuiBadge component with custom styles.
 * Refer to https://mui.com/material-ui/react-badge/ for more details.
 */
export function Badge(props: BadgeProps) {
  const {
    count,
    content,
    size = "medium",
    borderColor,
    backgroundColor,
    color,
    sx,
    ...rest
  } = props;

  return (
    <MuiBadge
      {...rest}
      badgeContent={content ?? count}
      size={size}
      sx={mergeSxProps(
        (theme: Theme) => ({
          "& .MuiBadge-badge": {
            ...(isDefined(borderColor) && {
              borderColor: borderColor(theme),
            }),

            ...(isDefined(backgroundColor) && {
              backgroundColor: backgroundColor(theme),
            }),

            ...(isDefined(color) && {
              color: color(theme),
            }),
          },
        }),
        sx
      )}
    />
  );
}
